import {
  SideBarWrapper,
  SearchInputWrapper,
} from '@hotjar/insights-common/components/SideBar/SideBar';
import { useTopNavHeight } from '@hotjar/webapp/hooks/useTopNavHeight';
import type { ReactNode } from 'react';
import { createContext } from 'react';
import styled from '@emotion/styled';
import { SearchInput, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';

export const SIDE_BAR_WIDTH = '256px';

const StyledSideBarWrapper = styled(SideBarWrapper)<{ topNavHeight: number }>`
  width: ${SIDE_BAR_WIDTH};
  max-width: ${SIDE_BAR_WIDTH};
  position: sticky;
  top: ${({ topNavHeight }) => topNavHeight}px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 15; /* Ensures that the drop down menus appear above the other page content */
  gap: ${tokens.spacer.x2};
`;

const SideBarChildren = styled.div`
  flex: 1;
  overflow-y: auto;

  /* Make the scrollbar appear in the padding area */
  padding: 0 ${tokens.spacer.x1pt5};
  margin: 0 -${tokens.spacer.x1pt5};
`;

export interface SideBarContextData {
  selectedWidgetId: number | undefined;
  renamingWidgetId: number | undefined;
  rename: ((widgetId: number, newName: string) => void) | undefined;
  cancelRename: (() => void) | undefined;
}

export interface SideBarProps {
  children: ReactNode;
  onSearchTextChange: (searchText: string) => void;
  searchText?: string;
  searchDisabled: boolean;
  sidebarContext: SideBarContextData;
}

export const SideBarContext = createContext<SideBarContextData>({
  selectedWidgetId: undefined,
  renamingWidgetId: undefined,
  rename: undefined,
  cancelRename: undefined,
});

export const SideBar = ({
  children,
  searchText,
  onSearchTextChange,
  searchDisabled,
  sidebarContext,
}: SideBarProps): JSX.Element => {
  const { t } = useTranslation('feedback', { keyPrefix: 'sidebar.search' });
  const topNavHeight = useTopNavHeight();
  return (
    <StyledSideBarWrapper topNavHeight={topNavHeight}>
      <SearchInputWrapper>
        <SearchInput
          placeholder={t('input-placeholder')}
          size="s"
          onChange={(event) => onSearchTextChange(event.currentTarget.value)}
          value={searchText}
          isDisabled={searchDisabled}
          clearActionProps={{
            description: t('clear-description'),
            actionText: t('clear-description'),
          }}
        />
      </SearchInputWrapper>
      <SideBarContext.Provider value={sidebarContext}>
        <SideBarChildren>{children}</SideBarChildren>
      </SideBarContext.Provider>
    </StyledSideBarWrapper>
  );
};
